import { PdfBrandConfig } from '@common/types';
import { compact } from 'lodash';

import { Content } from 'pdfmake/interfaces';
import { convertImageToBase64 } from './pdf-content-creator';

interface PdfContentProps {
  headerContent: Content;
  titleText: string;
  subtitleText: string;
  brandConfig: PdfBrandConfig;
}
export const getPdfBackCover = async ({
  titleText,
  subtitleText,
  headerContent,
  brandConfig,
}: PdfContentProps): Promise<Content> => {
  const leftDecorImage = brandConfig.topLeftTextDecor ? await convertImageToBase64(brandConfig.topLeftTextDecor) : '';
  const rightDecorImage = brandConfig.bottomRightTextDecor
    ? await convertImageToBase64(brandConfig.bottomRightTextDecor)
    : '';

  const header: Content = {
    text: headerContent,
    alignment: 'right',
    font: 'Inter',
    fontSize: 12,
    margin: [0, 0, 0, 0], // [left, top, right, bottom]
    color: 'black',
  };

  const leftBracket: Content | null = leftDecorImage
    ? {
        image: `data:image/png;base64,${leftDecorImage}`,
        width: 18,
        margin: [0, -10, 0, 0],
      }
    : null;

  // Center text
  const title: Content = {
    text: titleText,
    font: 'Inter',
    fontSize: 16,
    bold: true,
    color: '#330A89',
    alignment: 'center',
    margin: [0, 0, 0, 0],
  };

  // Right bracket, slightly lower with positive top margin
  const rightBracket: Content | null = rightDecorImage
    ? {
        image: `data:image/png;base64,${rightDecorImage}`,
        width: 18,
        margin: [0, 15, 0, 0], // Moves it down by 10 points
      }
    : null;

  // Use three columns: left bracket, center text, right bracket
  // - The middle column has '*' width, so it expands, pushing the brackets out.
  // - 'auto' keeps the brackets at their natural width.
  // - margin: [0, 200, 0, 0] places the entire row ~200 points from the top (adjust as needed).
  const bracketedRow: Content = {
    columns: compact([
      { width: '*', text: '' },
      leftBracket ? { width: 'auto', stack: [leftBracket], alignment: 'left' } : null,
      { width: 'auto', stack: [title] },
      rightBracket ? { width: 'auto', stack: [rightBracket], alignment: 'right' } : null,
      { width: '*', text: '' },
    ]),
    alignment: 'center',
    margin: [0, 200, 0, 0],
    columnGap: 5, // Spacing between columns
  };

  // Message below the title.
  const subtitle: Content = {
    text: subtitleText,
    alignment: 'center',
    font: 'Inter',
    fontSize: 12,
    margin: [60, 30, 60, 0],
    color: 'black',
  };

  return compact([header, bracketedRow, subtitle]);
};
